import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from 'react';
import { ContentWrapper } from './content-wrapper';
import { ModuleLandingCompact } from './module-landing-compact';
import { useFavorites } from '../../hooks/use-favorites';
/**
 * Convert API menu tree to CompactLandingCategory format
 */
function convertApiMenusToCategories(menus, modulePath, moduleName, staticCategories) {
    // Find the module in the API response
    const module = menus.find(m => m.path === modulePath ||
        m.name === moduleName ||
        m.name === modulePath.replace('/', ''));
    if (!module || !module.children || module.children.length === 0) {
        // If no API data, return empty categories (user has no access)
        return [];
    }
    // Map API categories to CompactLandingCategory
    const categories = [];
    module.children.forEach(category => {
        // Find matching static category config for styling (icon, color, etc.)
        const categoryName = category.name.replace(`${moduleName}-`, '').replace(`${modulePath.replace('/', '')}-`, '');
        const staticCategory = staticCategories.find(sc => sc.id === categoryName ||
            sc.title.toLowerCase() === category.displayName.toLowerCase() ||
            (category.displayName === 'Transactions' && sc.id === 'process'));
        if (category.children && category.children.length > 0) {
            // Build items from API children
            const items = category.children
                .filter(item => item.path) // Only include items with paths
                .map(item => {
                // Find matching static item for icon
                const staticItem = staticCategory?.items.find(si => si.path === item.path);
                return {
                    label: item.displayName,
                    path: item.path,
                    description: item.description || staticItem?.description || '',
                    icon: staticItem?.icon
                };
            });
            if (items.length > 0) {
                categories.push({
                    id: categoryName,
                    title: category.displayName === 'Transactions' ? 'Process' : category.displayName,
                    description: category.description || staticCategory?.description || '',
                    icon: staticCategory?.icon,
                    color: staticCategory?.color || 'gray',
                    items
                });
            }
        }
    });
    return categories;
}
/**
 * CommonModuleLanding - A reusable landing page component for different modules
 *
 * Supports:
 * - Static categories from config
 * - API-based menus with loading state
 * - Access control filtering
 * - Favorites with module-specific storage
 * - Configurable breadcrumbs and navigation
 */
export function CommonModuleLanding({ config, favoritesKey, modulePath, moduleName, breadcrumbLabel, validRoutes, onNavigate, onNavigateToDashboard, useApiMenus = false, fetchMenus, hasAccess }) {
    const [categories, setCategories] = useState(useApiMenus ? [] : config.categories);
    const [loading, setLoading] = useState(useApiMenus);
    // Use the favorites hook with module-specific key
    const { favorites, toggleFavorite } = useFavorites(favoritesKey);
    // Fetch menus using provided fetch function (when API menus enabled)
    useEffect(() => {
        if (!useApiMenus || !fetchMenus)
            return;
        const loadMenus = async () => {
            try {
                setLoading(true);
                const data = await fetchMenus();
                const convertedCategories = convertApiMenusToCategories(data.menus, modulePath, moduleName || modulePath.replace('/', ''), config.categories);
                setCategories(convertedCategories);
            }
            catch (error) {
                console.error('Error loading menus:', error);
                setCategories([]);
            }
            finally {
                setLoading(false);
            }
        };
        loadMenus();
    }, [useApiMenus, fetchMenus, modulePath, moduleName, config.categories]);
    // Filter categories based on user access
    const filteredCategories = useMemo(() => {
        const categoriesToFilter = useApiMenus ? categories : config.categories;
        if (!hasAccess) {
            return categoriesToFilter;
        }
        return categoriesToFilter
            .map(category => {
            // Filter items within each category
            const filteredItems = category.items.filter(item => hasAccess(item.path));
            // Only include category if it has accessible items
            if (filteredItems.length === 0) {
                return null;
            }
            return {
                ...category,
                items: filteredItems,
            };
        })
            .filter((category) => category !== null);
    }, [hasAccess, categories, config.categories, useApiMenus]);
    const handleNavigation = (path) => {
        // Extract base path without query parameters for validation
        const basePath = path.split('?')[0];
        // Check if the base path is a valid route
        if (validRoutes.includes(basePath)) {
            // Navigate with the full path including query parameters
            onNavigate(path);
        }
        else {
            // Show coming soon message for dummy/unimplemented routes
        }
    };
    const handleFavoriteToggle = (path, isFavorite) => {
        toggleFavorite(path, isFavorite);
    };
    // Loading state (only for API-based menus)
    if (loading) {
        return (_jsx(ContentWrapper, { breadcrumbs: [
                { label: 'Home', onClick: onNavigateToDashboard },
                { label: breadcrumbLabel }
            ], noPadding: true, children: _jsx("div", { className: "h-full flex items-center justify-center", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2" }), _jsxs("p", { className: "text-gray-500", children: ["Loading ", breadcrumbLabel, "..."] })] }) }) }));
    }
    return (_jsx(ContentWrapper, { breadcrumbs: [
            { label: 'Home', onClick: onNavigateToDashboard },
            { label: breadcrumbLabel }
        ], noPadding: true, children: _jsx("div", { className: "flex-1 min-h-0", children: _jsx(ModuleLandingCompact, { title: config.title, description: config.description, categories: filteredCategories, onNavigate: handleNavigation, showSearch: config.showSearch, showViewToggle: config.showViewToggle, showExpandCollapseAll: config.showExpandCollapseAll, showFavorites: config.showFavorites, initialFavorites: favorites, onFavoriteToggle: handleFavoriteToggle, defaultView: config.defaultView, maxColumns: config.maxColumns, className: "h-full" }) }) }));
}
